import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';
import { toDate } from 'shared/utils/date-utils';

import ClientSecret from 'features/gateway/domain/models/client-secret';
import Gateway from 'features/gateway/domain/models/gateway';
import GatewayMonitoringFilter from 'features/gateway/domain/models/gateway-monitoring-filter';
import { GatewayType } from 'features/gateway/domain/models/gateway-type';
import { ReadGatewaysQuery } from 'features/gateway/domain/models/read-gateways-query';
import { ReadGatewaysResponse } from 'features/gateway/domain/models/read-gateways-response';
import ValidateGatewayIpAddressCommand from 'features/gateway/domain/models/validate-gateway-ipaddress-command';
import ValidateGatewayNameCommand from 'features/gateway/domain/models/validate-gateway-name-command';
import ValidateSingleMediaServerGatewayCommand from '../models/validate-single-media-server-gateway-command';

const baseUrl = '/api/v1/Gateway';

enum GatewayQueryPaths {
  CreateGateway = 'CreateGateway',
  ReadGateways = 'ReadGateways',
  ReadGateway = 'ReadGateway',
  ReadGatewayOverview = 'ReadGatewayOverview',
  GetMonitoringFilters = 'GetMonitoringFilters',
  UpdateGateway = 'UpdateGateway',
  DeleteGateway = 'DeleteGateway',
  GenerateSecrets = 'GenerateSecrets',
  ValidateGatewayName = 'ValidateGatewayName',
  ValidateGatewayIpAddress = 'ValidateGatewayIpAddress',
  ValidateSingleMediaServerGateway = 'ValidateSingleMediaServerGateway',
}

const createGateway = async (axiosInstance: AxiosInstance, gateway: Gateway): Promise<Gateway> => {
  const body = JSON.stringify(gateway);
  const response = await axiosInstance.post(`${baseUrl}/${GatewayQueryPaths.CreateGateway}`, body);
  return readGateway(axiosInstance, response.data.id);
};

const readGateways = async (axiosInstance: AxiosInstance, query: ReadGatewaysQuery): Promise<ReadGatewaysResponse> => {
  const body = JSON.stringify(query);
  const response = await axiosInstance.post(`${baseUrl}/${GatewayQueryPaths.ReadGatewayOverview}`, body);
  return response.data;
};

const readGateway = async (axiosInstance: AxiosInstance, gatewayId: string, signal?: AbortSignal): Promise<Gateway> => {
  const response = await axiosInstance.get(`${baseUrl}/${GatewayQueryPaths.ReadGateway}/${gatewayId}`, { signal });
  const gateway = response.data as Gateway;
  let childGateways = gateway.childGateways;

  if ((gateway.type === GatewayType.IoTEdgeCluster || gateway.type === GatewayType.IoTEdgeMediaServerGatewayCluster) && childGateways) {
    childGateways = [...childGateways]
      .sort((firstGateway, secondGateway) => Number(secondGateway.isActiveInCluster) - Number(firstGateway.isActiveInCluster))
      .map((childGateway) => ({
        ...childGateway,
        lastCommunicationDate: toDate(childGateway.lastCommunicationDate),
      }));
  }

  return {
    ...gateway,
    childGateways,
    lastCommunicationDate: toDate(gateway.lastCommunicationDate),
  };
};

const getMonitorFilters = async (axiosInstance: AxiosInstance, signal: AbortSignal): Promise<GatewayMonitoringFilter[]> => {
  const response = await axiosInstance.get(`${baseUrl}/${GatewayQueryPaths.GetMonitoringFilters}`, { signal });
  return response.data;
};

const updateGateway = async (axiosInstance: AxiosInstance, gateway: Gateway): Promise<void> => {
  const body = JSON.stringify(gateway);
  await axiosInstance.put(`${baseUrl}/${GatewayQueryPaths.UpdateGateway}`, body);
};

const deleteGateway = async (axiosInstance: AxiosInstance, id: string): Promise<void> => {
  await axiosInstance.delete(`${baseUrl}/${GatewayQueryPaths.DeleteGateway}/${id}`);
};

const generateSecrets = async (axiosInstance: AxiosInstance, gatewayId: string): Promise<ClientSecret> => {
  const body = JSON.stringify({ gatewayId });
  const response = await axiosInstance.post(`${baseUrl}/${GatewayQueryPaths.GenerateSecrets}`, body);
  return response.data;
};

export const validateGatewayName = async (axiosInstance: AxiosInstance, command: ValidateGatewayNameCommand): Promise<void> => {
  const body = JSON.stringify(command);
  await axiosInstance.post(`${baseUrl}/${GatewayQueryPaths.ValidateGatewayName}`, body);
};

export const validateGatewayIpAddress = async (axiosInstance: AxiosInstance, command: ValidateGatewayIpAddressCommand): Promise<void> => {
  const body = JSON.stringify(command);
  await axiosInstance.post(`${baseUrl}/${GatewayQueryPaths.ValidateGatewayIpAddress}`, body);
};

export const validateSingleMediaServerGateway = async (
  axiosInstance: AxiosInstance,
  command: ValidateSingleMediaServerGatewayCommand,
): Promise<void> => {
  const body = JSON.stringify(command);
  await axiosInstance.post(`${baseUrl}/${GatewayQueryPaths.ValidateSingleMediaServerGateway}`, body);
};

export const useCreateGateway = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [GatewayQueryPaths.CreateGateway],
    mutationFn: (gateway: Gateway) => createGateway(authenticatedInstance, gateway),
    onSuccess: (gateway) => {
      queryClient.setQueryData([GatewayQueryPaths.ReadGateway, gateway.id], gateway);
    },
  });
};
export const useReadGateways = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [GatewayQueryPaths.ReadGateways],
    mutationFn: (query: ReadGatewaysQuery) => readGateways(authenticatedInstance, query),
  });
};

export const useReadGateway = (gatewayId: string | undefined) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery<Gateway, Error>({
    queryKey: [GatewayQueryPaths.ReadGateway, gatewayId],
    queryFn: ({ signal }) => readGateway(authenticatedInstance, gatewayId!, signal),
    enabled: Boolean(gatewayId),
  });
};

export const useGetMonitorFilters = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery<GatewayMonitoringFilter[], unknown>({
    queryKey: [GatewayQueryPaths.GetMonitoringFilters],
    queryFn: ({ signal }) => getMonitorFilters(authenticatedInstance, signal),
  });
};

export const useUpdateGateway = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [GatewayQueryPaths.UpdateGateway],
    mutationFn: (gateway: Gateway) => updateGateway(authenticatedInstance, gateway),
    onSuccess: (_, gateway) => {
      queryClient.invalidateQueries({ queryKey: [GatewayQueryPaths.ReadGateway, gateway.id] });
    },
  });
};
export const useDeleteGateway = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [GatewayQueryPaths.DeleteGateway],
    mutationFn: (id: string) => deleteGateway(authenticatedInstance, id),
  });
};
export const useGenerateSecrets = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [GatewayQueryPaths.GenerateSecrets],
    mutationFn: (gatewayId: string) => generateSecrets(authenticatedInstance, gatewayId),
  });
};
